import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  RangeInput,
  Spinner,
} from "grommet";
import API_URL from "../../config/config";
import { Info } from "grommet-icons";
import adminAxios from "../../config/adminAxios";
export const QuizFields = ({ onSubmit, data }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skills, setSkills] = useState([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [skillsError, setSkillsError] = useState(null);
  const [skillQuestionCounts, setSkillQuestionCounts] = useState([]);
  const [submittedData, setSubmittedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = data;
  const courseSelectionStorageKey = `skillCourseSelections:${userId}`;
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await adminAxios.get(
          `${API_URL}/user/getUserProductSkills/${userId}`,
        );
        setSkills(response.data.skills);
        setIsLoadingSkills(false);
      } catch (error) {
        setSkillsError(error.message);
        setIsLoadingSkills(false);
      }
    };

    fetchSkills();
  }, [userId]);
  const getMin = () => {
    return skills.length * 5; // Ensure a minimum of 5 questions per skill
  };

  const getMax = () => {
    return 10 * skills.length; // Maximum total questions based on skills length
  };

  const initializeSkillQuestionCounts = () => {
    const numSkills = skills.length;
    const initialValue = 5; // Starting each skill with a minimum of 5 questions
    const remainder = getMin() % numSkills;

    const initialCounts = Array(numSkills).fill(initialValue);
    for (let i = 0; i < remainder; i++) {
      initialCounts[i] += 1;
    }

    setSkillQuestionCounts(initialCounts);
  };

  useEffect(() => {
    if (skills.length > 0) {
      initializeSkillQuestionCounts();
    }
  }, [skills]);

  const handleSkillSliderChange = (index, newValue) => {
    const currentTotal = skillQuestionCounts.reduce((a, b) => a + b, 0);
    const maxTotal = getMax();
    const minTotal = getMin();
    const remainingSkills = skills.length - 1;

    const maxAllowedForSkill = Math.min(10, maxTotal - remainingSkills);
    let newCounts = [...skillQuestionCounts];

    // Prevent values lower than 5
    if (newValue < 5) {
      return;
    }

    // Check if decreasing the slider would bring total below minTotal
    const newTotalWithDecreasedValue =
      currentTotal - (skillQuestionCounts[index] - newValue);

    if (newTotalWithDecreasedValue < minTotal) {
      return;
    }

    // Ensure the selected skill does not exceed the allowed max (and max 10 per skill)
    newCounts[index] = Math.min(newValue, maxAllowedForSkill);

    const newTotal = newCounts.reduce((a, b) => a + b, 0);

    // If the total exceeds the max allowed, adjust other sliders
    if (newTotal > maxTotal) {
      let remainingDelta = newTotal - maxTotal;

      for (let i = 0; i < newCounts.length; i++) {
        if (i !== index && remainingDelta > 0) {
          const adjustableAmount = Math.min(remainingDelta, newCounts[i] - 5); // Ensure at least 5 questions remain
          newCounts[i] -= adjustableAmount;
          remainingDelta -= adjustableAmount;
        }
      }
    }

    setSkillQuestionCounts(newCounts);
  };

  const handleSubmit = () => {
    setLoading(true);
    setIsSubmitting(true);

    let courseSelectionsBySkill = {};
    try {
      courseSelectionsBySkill =
        JSON.parse(localStorage.getItem(courseSelectionStorageKey)) || {};
    } catch (error) {
      console.error("Error reading course selections:", error);
    }

    const skillDistribution = skills.map((skill, index) => {
      const selectedCourses = courseSelectionsBySkill[skill.sub_product_id];
      const courseIds = Array.isArray(selectedCourses)
        ? selectedCourses
            .map((courseId) => Number(courseId))
            .filter((courseId) => Number.isInteger(courseId))
        : [];
      return {
        skillId: skill.sub_product_id,
        count: skillQuestionCounts[index] || 0,
        ...(courseIds.length > 0 && { courseIds }),
      };
    });

    setSubmittedData(skillDistribution);

    onSubmit({
      totalQuestions: skillQuestionCounts.reduce((a, b) => a + b, 0),
      skillDistribution,
    });

    setIsSubmitting(false);
    setLoading(false);
  };
  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Processing...</Text>
      </Box>
    );
  }
  const totalQuestions = skillQuestionCounts.reduce((a, b) => a + b, 0); // Calculate total questions

  return (
    <Box
      pad={{ vertical: "medium", horizontal: "large" }}
      gap="medium"
      align="center"
    >
      <Heading level="2">Refine your skill assessment</Heading>

      {isLoadingSkills ? (
        <Text>Loading skills...</Text>
      ) : skillsError ? (
        <Text color="status-critical">Error: {skillsError}</Text>
      ) : (
        <>
          <Box width="large" margin={{ bottom: "medium" }}>
            <Box
              pad="medium"
              background="light-2"
              round="small"
              margin="medium"
            >
              <Text weight="highlight">
                <Info color="brand" /> <b>Note</b> that you can select a{" "}
                <b>minimum of 5 </b>
                questions and a <b>maximum of 10</b> questions for each skill.
              </Text>
              <Text weight="highlight">
                <Info color="brand" /> The more questions you answer, the more
                tailored your learning recommendation will be from the Digital
                Skills Advisor tool.
              </Text>
            </Box>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Skill(s)</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Number of questions</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skills.map((skill, index) => (
                  <TableRow key={skill.sub_product_id}>
                    <TableCell>
                      {skill.product_name} - {skill.sub_product_name}
                    </TableCell>
                    <TableCell>
                      <Box
                        direction="row"
                        align="center"
                        justify="between"
                        style={{ width: "100%" }}
                      >
                        <Text>{5}</Text> {/* Minimum number */}
                        <Box
                          style={{
                            flex: 1,
                            margin: "0 10px",
                            position: "relative",
                          }}
                        >
                          {" "}
                          {/* Flex container for the slider */}
                          <RangeInput
                            value={skillQuestionCounts[index] || 5}
                            min={5} // Set the minimum to 5
                            max={Math.min(10, getMax() - (skills.length - 1))}
                            onChange={(e) =>
                              handleSkillSliderChange(
                                index,
                                parseInt(e.target.value, 10),
                              )
                            }
                          />
                          <Text alignSelf="center">
                            {skillQuestionCounts[index] || 5} questions
                          </Text>
                        </Box>
                        <Text>
                          {Math.min(10, getMax() - (skills.length - 1))}
                        </Text>{" "}
                        {/* Maximum number */}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {skills.length >= 2 && (
            <Text weight="bold">
              Total number of questions: {totalQuestions}
            </Text>
          )}
          <Box width="medium" pad="medium">
            <Button
              primary
              onClick={handleSubmit}
              label={isSubmitting ? "Starting Assessment..." : "Continue"}
              disabled={isSubmitting}
            />
          </Box>
        </>
      )}

      {submittedData && (
        <Box margin={{ top: "medium" }}>
          <Heading level="4">Submitted Skill Distribution</Heading>
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell scope="col">
                  <Text>Skill ID</Text>
                </TableCell>
                <TableCell scope="col">
                  <Text>Question Count</Text>
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submittedData.map((item) => (
                <TableRow key={item.skillId}>
                  <TableCell>{item.skillId}</TableCell>
                  <TableCell>{item.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
